export type PropertyPath = PropertyKey | readonly PropertyKey[];
export type Customizer = (
    objectValue: any,
    sourceValue: any,
    key: string,
    object: Record<string, any>,
    source: Record<string, any>
) => any;

export const blockedPathSegments: ReadonlySet<string> = new Set([
    '__proto__',
    'constructor',
    'prototype',
]);

export function toKey(value: PropertyKey): string | symbol {
    return typeof value === 'symbol'
        ? value
        : value === 0 && 1 / Number(value) === -Infinity
          ? '-0'
          : String(value);
}

export function isIndex(value: PropertyKey, length: number = Number.MAX_SAFE_INTEGER): boolean {
    return (
        typeof value !== 'symbol' &&
        /^(?:0|[1-9]\d*)$/.test(String(value)) &&
        Number(value) < length
    );
}

export function toPath(path: PropertyPath, object?: unknown): Array<string | symbol> {
    if (Array.isArray(path)) {
        return path.map(toKey);
    }
    const key = toKey(path as PropertyKey);
    if (
        typeof key === 'symbol' ||
        !/[.[\]]/.test(key) ||
        (object != null && key in Object(object))
    ) {
        return [key];
    }
    const segments: string[] = key.startsWith('.') ? [''] : [];
    key.replace(
        /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:\\.|(?!\2)[^\\])*)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g,
        (match, numeric: string | undefined, quote: string | undefined, quoted: string) => {
            segments.push(quote ? quoted.replace(/\\(\\)?/g, '$1') : (numeric ?? match));
            return match;
        }
    );
    return segments;
}

export function isObject<T>(value: T): value is T & Record<string, any> {
    return value !== null && typeof value === 'object';
}

export function isMergeable(value: unknown): value is Record<string, any> | any[] {
    if (Array.isArray(value)) {
        return true;
    }
    if (!isObject(value)) {
        return false;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}

export function enumerableOwnKeys(value: object): PropertyKey[] {
    return [
        ...Object.keys(value),
        ...Object.getOwnPropertySymbols(value).filter(symbol =>
            Object.prototype.propertyIsEnumerable.call(value, symbol)
        ),
    ];
}

export function enumerableStringKeysIn(value: object): string[] {
    const keys: string[] = [];
    const visited = new Set<string>();
    for (let current: object | null = value; current; current = Object.getPrototypeOf(current)) {
        for (const key of Object.getOwnPropertyNames(current)) {
            if (!visited.has(key)) {
                visited.add(key);
                if (Object.prototype.propertyIsEnumerable.call(current, key)) {
                    keys.push(key);
                }
            }
        }
    }
    return keys;
}

export function enumerableKeysIn(value: object): PropertyKey[] {
    const keys: PropertyKey[] = enumerableStringKeysIn(value);
    for (let current: object | null = value; current; current = Object.getPrototypeOf(current)) {
        keys.push(
            ...Object.getOwnPropertySymbols(current).filter(symbol =>
                Object.prototype.propertyIsEnumerable.call(current, symbol)
            )
        );
    }
    return keys;
}

export function assignValue(object: object, key: PropertyKey, value: unknown): void {
    if (Object.prototype.hasOwnProperty.call(object, key)) {
        const current: unknown = Reflect.get(object, key);
        if (current === value || (current !== current && value !== value)) {
            return;
        }
    }
    if (key === '__proto__') {
        Object.defineProperty(object, key, {
            configurable: true,
            enumerable: true,
            value,
            writable: true,
        });
    } else {
        (object as Record<PropertyKey, unknown>)[key] = value;
    }
}
